/**
 * Wave 29.10.l6 — Browser-side image compression for uploads.
 *
 * Why
 * ───
 * Modern phone cameras shoot 8-12 MB JPEGs. When a tech selects 14 photos
 * on a JC and they all upload in parallel through `uploadManager`, the pod
 * gets ~100 MB simultaneously and Cloudflare returns 520. This module
 * downscales large images before they ever hit the network.
 *
 * Iter 333 — 2026-02-24 · Tightened defaults after a production incident:
 *   User uploaded 38 photos to a JC. Client-side compression brought each
 *   down to ~2 MB (2048px @ q0.85), but the ORIGIN was still taking
 *   ~20 seconds per file (put_object → PIL thumb → PIL display → put ×2
 *   → DB insert). Cloudflare's edge timeout is 100s, so once the queue
 *   backed up, every subsequent upload 524'd with "The origin web
 *   server did not respond to Cloudflare within the allowed time."
 *
 *   Fix: shrink further at the client. 1600px is still ~3× the largest
 *   phone display width and produces sharp print output. JPEG q0.78 is
 *   visually indistinguishable for documentation photos (job-site
 *   evidence, not fine-art). Threshold dropped to 500 KB so even
 *   already-compressed phone shots get an extra pass.
 *
 *   Typical 8 MB phone photo → 250-450 KB. Upload transfer + full
 *   backend processing goes from ~20s to ~4-5s per file. 38 photos
 *   at 2× parallelism now completes in ~90s instead of ~380s.
 *
 * Safe defaults
 * ─────────────
 * - Non-image files are returned untouched.
 * - HEIC/HEIF and SVG are returned untouched (canvas can't decode them
 *   reliably; we let the original through and trust the backend).
 * - Files <= COMPRESS_THRESHOLD bytes are returned untouched.
 * - If anything goes wrong (CORS, OOM, weird codec), we return the
 *   ORIGINAL file instead of throwing. The upload should never fail
 *   because compression failed.
 */

const COMPRESS_THRESHOLD = 500 * 1024;  // [Iter 333] was 1 MB → 500 KB
const MAX_EDGE = 1600;                  // [Iter 333] was 2048 → 1600
const JPEG_QUALITY = 0.78;              // [Iter 333] was 0.85 → 0.78

const SKIP_TYPES = new Set([
  "image/heic", "image/heif",
  "image/svg+xml",
  "image/gif", // animated frames lost
]);

/** Try-hard compress. Returns a new File on success or the original on any failure. */
export async function compressImage(file) {
  try {
    if (!file || !(file instanceof Blob)) return file;
    const type = (file.type || "").toLowerCase();
    if (!type.startsWith("image/")) return file;
    if (SKIP_TYPES.has(type)) return file;
    if (file.size <= COMPRESS_THRESHOLD) return file;

    const bitmap = await _toBitmap(file);
    if (!bitmap) return file;
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    const scale = longest > MAX_EDGE ? (MAX_EDGE / longest) : 1;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (typeof bitmap.close === "function") bitmap.close();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob || blob.size >= file.size) {
      // No win — original was already small / unusual encoding.
      return file;
    }
    const newName = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: file.lastModified || Date.now() });
  } catch {
    return file;
  }
}

async function _toBitmap(blob) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(blob);
    } catch { /* fall through */ }
  }
  // Fallback for older Safari: load via Image.
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}
